import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import Cookies from 'universal-cookie';

import { IVenueDetails } from 'models/i-venue-details.interface';
import { IAuctionHouseBidSubmitRequest } from 'models/market/i-auction-house-bid-submit-request.model';
import { IMapMarketOfferToBidRequest } from 'models/market/i-map-market-offer-to-bid-request.interface';
import { IMarketFiltersContract } from 'models/market/i-market-filters-contract.interface';
import { IMarketFilters } from 'models/market/i-market-filters.interface';
import { IMarketTradesData, IMarketTradesPayload } from 'models/market/i-market-trades-payload.interface';
import { MarketStatus } from 'enums/market/market-status.enum';
import { TradeContractType } from 'enums/market/trade-contract-type.enum';
import { ITradeBidSubmitRequest } from 'models/market/i-trade-bid-submit-request.model';
import { ITradeAsset, ITradeDetails } from 'models/trade/i-trade-details.interface';
import { ITradeRequest } from 'models/trade/i-trade-request.interface';

import { CookiesKeys } from 'enums/cookies-keys.enum';

import { ASSET_FIELDS, ASSET_TRADE_FIELDS } from 'constants/params-fields.constant';
import { WEB3 } from 'constants/web3.constant';

import { getBooleanEnvValue } from 'utils/env.utils';
import { getQueryParams } from 'utils/get-query-params.utils';

import { getMarketOffersMock } from './mocks.service';

export const adaptStateHash = (state_hash: string) => (state_hash === '0x0' ? WEB3.nullStateHash : state_hash);
export const isNull = (value: any) => value === null;

// The marketplace shows only the P2P `trade` contract type (0). Auction-house (1,
// deprecated) and bid2earn (2) are filtered out server-side via `contract_type=0`
// on the /trades/list request, so no client-side contract-type filtering is needed.
const MARKET_CONTRACT_TYPE = TradeContractType.trade.toString();

let cancelToken: CancelTokenSource;

const mapMarketFilters = (filters: IMarketFilters): IMarketFiltersContract => ({
  advanced_search: filters.search,
  show_type: filters.statuses?.join(',') || '',
  ordering: filters.ordering,
  currency: filters.priceCurrency ? filters.priceCurrency.toString() : '',
  max_price: filters.pricesMax ? filters.pricesMax.toString() : '',
  min_price: filters.pricesMin ? filters.pricesMin.toString() : '',
  page_size: filters.limit.toString(),
  page: filters.page.toString(),
});

export const mapMarketOfferToBidRequest = (configuration: IMapMarketOfferToBidRequest): ITradeRequest => {
  return {
    nonce: configuration.nonce.toString(),
    expirationTime: configuration.expirationTime.toString(),
    tradeTokenAddress: configuration.currencyAddress,
    itemsTokenAddress: configuration.tradeDetails!.asset.contract.address,
    items: [
      {
        tokenId: configuration.tradeDetails!.asset.token_id,
        amount: +configuration.tradeDetails!.amount,
        stateHash: adaptStateHash(configuration.tradeDetails!.state_hash),
      },
    ],
    bidValue: configuration.bidValue.toString(),
    to: WEB3.nullAddress,
  };
};

export const mapMarketOfferToTradeRequest = (tradeDetails: ITradeDetails, currencyAddress: string): ITradeRequest => {
  return {
    nonce: tradeDetails!.nonce.toString(),
    expirationTime: tradeDetails!.expiration_time.toString(),
    tradeTokenAddress: currencyAddress,
    itemsTokenAddress: tradeDetails!.asset.contract.address,
    items: [
      {
        tokenId: tradeDetails!.asset.token_id,
        amount: +tradeDetails!.amount,
        stateHash: adaptStateHash(tradeDetails!.state_hash),
      },
    ],
    offerValue: tradeDetails!.price,
    to: WEB3.nullAddress,
  };
};

export const fetchMarketOffersService = async (
  filters: IMarketFilters
): Promise<AxiosResponse<IMarketTradesData | null>> => {
  const useMarketMocks = getBooleanEnvValue(process.env.REACT_APP_USE_MARKET_MOCKS);

  if (useMarketMocks) {
    return getMarketOffersMock();
  }

  if (typeof cancelToken != typeof undefined) {
    cancelToken.cancel('Operation canceled due to new request.');
  }

  cancelToken = axios.CancelToken.source();

  const mappedFilters = mapMarketFilters(filters);
  const params = getQueryParams({ ...mappedFilters, contract_type: MARKET_CONTRACT_TYPE });
  const noDataResponse = {
    totalPages: 0,
    totalCount: 0,
    currentPage: 1,
    results: [],
  };

  const tradesPayload = await axios.get<IMarketTradesPayload>(`/api/v1/trades/list`, {
    params,
  });

  if (!tradesPayload.data?.count) {
    return {
      ...tradesPayload,
      data: noDataResponse,
    };
  }

  const assetIds = tradesPayload.data.results.map(({ asset_id }) => asset_id);
  const ids = [...new Set(assetIds)];
  const assetsPayload = await fetchAllAssets(ids, true);

  if (!assetsPayload?.data) {
    return {
      ...assetsPayload,
      data: noDataResponse,
    };
  }

  const assetMap = new Map(assetsPayload.data.map((a) => [a.id, a]));
  const mappedResults = tradesPayload.data.results
    .filter((trade) => assetMap.has(trade.asset_id))
    .map((trade) => ({
      ...trade,
      asset: assetMap.get(trade.asset_id)!,
    }));

  const totalCount = mappedResults.length;
  const totalPages = totalCount ? Math.ceil(totalCount / filters.limit) : 1;

  return {
    ...tradesPayload,
    data: {
      totalCount,
      totalPages,
      currentPage: filters.page,
      results: mappedResults,
    },
  };
};

// NOTE: Bid2Earn auctions are intentionally NOT surfaced on the P2P marketplace.
// They live only on /home/bid2earn (and the dashboard live-market). The market
// requests `contract_type=0` so /trades/list returns only P2P trades, and the
// "Current Min" pricing excludes bid2earn (see MarketList → getLowestPerShareEntry).

export const fetchAllMarketTrades = async (params: {
  statuses?: MarketStatus[];
  search?: string;
}): Promise<AxiosResponse<IMarketTradesData | null>> => {
  const PAGE_SIZE = 100;
  const baseParams = {
    show_type: params.statuses?.join(',') || '',
    advanced_search: params.search || '',
    contract_type: MARKET_CONTRACT_TYPE,
    page_size: PAGE_SIZE.toString(),
  };

  const firstPageResponse = await axios.get<IMarketTradesPayload>(`/api/v1/trades/list`, {
    params: getQueryParams({ ...baseParams, page: '1' }),
  });

  let allTradeStubs: IMarketTradesPayload['results'] = [];
  if (firstPageResponse.data?.count) {
    const totalPages = Math.ceil(firstPageResponse.data.count / PAGE_SIZE);
    allTradeStubs = firstPageResponse.data.results;

    if (totalPages > 1) {
      const remainingPages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          axios.get<IMarketTradesPayload>(`/api/v1/trades/list`, {
            params: getQueryParams({ ...baseParams, page: (i + 2).toString() }),
          })
        )
      );
      for (const page of remainingPages) {
        allTradeStubs = [...allTradeStubs, ...page.data.results];
      }
    }
  }

  const assetIds = [...new Set(allTradeStubs.map(({ asset_id }) => asset_id))];
  const assetsPayload = assetIds.length ? await fetchAllAssets(assetIds, true) : null;
  const assetMap = new Map((assetsPayload?.data ?? []).map((a) => [a.id, a]));
  const mappedResults = allTradeStubs
    .filter((trade) => assetMap.has(trade.asset_id))
    .map((trade) => ({
      ...trade,
      asset: assetMap.get(trade.asset_id)!,
    }));

  return {
    ...firstPageResponse,
    data: { totalCount: mappedResults.length, totalPages: 1, currentPage: 1, results: mappedResults },
  };
};

export const fetchAllAssets = (
  ids: number[] | string[],
  includePricingTrades = false
): Promise<AxiosResponse<ITradeAsset[]>> => {
  // pricing_trades carries per-listing contract_type/amount so the market card can
  // aggregate contract_type-0 (standard TRADE) shares only. It is opt-in because it
  // is a heavy per-listing payload only the market card reads.
  const filters = includePricingTrades
    ? [...ASSET_FIELDS, ...ASSET_TRADE_FIELDS, 'pricing_trades']
    : [...ASSET_FIELDS, ...ASSET_TRADE_FIELDS];
  return axios.get<ITradeAsset[]>(`/api/v1/assets/all_assets`, {
    params: { ids: ids.join(','), filters: filters.join(',') },
  });
};

export const fetchMarketOfferById = (id: string): Promise<AxiosResponse<IVenueDetails, any>> => {
  return axios.get<IVenueDetails>(`/backend/nft/${id}`);
};

export const submitTradeBidService = (
  id: number,
  tradeBidData: ITradeBidSubmitRequest
): Promise<AxiosResponse<any>> => {
  return axios.post(`/api/v1/trades/${id}/offers/`, tradeBidData, {
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });
};

export const cancelBid = (id: number): Promise<AxiosResponse<number>> => {
  return axios
    .delete(`/api/v1/trades/offers/${id}`, {
      headers: {
        'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
      },
    })
    .then((data) => {
      return {
        ...data,
        data: id,
      };
    });
};

export const submitAuctionHouseBidService = (
  tradeBidData: IAuctionHouseBidSubmitRequest
): Promise<AxiosResponse<any>> => {
  return axios.post(`/api/v1/trades/bid`, tradeBidData, {
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });
};

export const postScanTriggerRequestService = (): Promise<AxiosResponse> => {
  return axios.post(`/api/v1/trades/scan`, null, {
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });
};
